/**
 * FeedbackFab component for SnapHub PWA.
 *
 * Renders a Floating Action Button (FAB) fixed at the bottom-right corner
 * of the viewport. The FAB is only visible for authenticated users with
 * role `admin` or `employee`. Clicking the FAB toggles a feedback form
 * panel above it.
 *
 * Validates: Requirements 1.1, 1.2, 1.3, 1.4, 2.1, 2.2, 2.3, 2.4, 2.5, 2.6
 */

import { eventBus } from '../../infrastructure/event-bus';
import { storageAdapter } from '../../infrastructure/storage-adapter';
import { decodeTokenPayload, resolveRoleFromPayload } from '../auth/auth.service';
import { renderFeedbackForm, resetFeedbackForm } from './feedback-form';
import { userPreferencesService } from '../../shared/services/user-preferences.service';
import type { Unsubscribe } from '../../infrastructure/event-bus';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Roles that are allowed to see the FAB. */
const ALLOWED_ROLES: ReadonlySet<string> = new Set(['admin', 'employee']);

// ---------------------------------------------------------------------------
// SVG Icons (inline)
// ---------------------------------------------------------------------------

const FEEDBACK_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`;

const CLOSE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;

// ---------------------------------------------------------------------------
// Module state
// ---------------------------------------------------------------------------

let fabContainer: HTMLDivElement | null = null;
let fabButton: HTMLButtonElement | null = null;
let dismissButton: HTMLButtonElement | null = null;
let formPanel: HTMLDivElement | null = null;
let isOpen = false;
let isDismissed = false;
let unsubscribeAuth: Unsubscribe | null = null;
let unsubscribePrefs: (() => void) | null = null;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Check whether the current user has an allowed role.
 * Returns `true` for `admin` or `employee`, `false` otherwise.
 */
function hasAllowedRole(): boolean {
  const token = storageAdapter.getAccessToken();
  if (!token) return false;

  const payload = decodeTokenPayload(token);
  if (!payload) return false;

  return ALLOWED_ROLES.has(resolveRoleFromPayload(payload));
}

/**
 * Update FAB visibility based on the current auth state, dismiss state,
 * and persistent user preference.
 */
function updateVisibility(): void {
  if (!fabContainer) return;

  if (isDismissed || userPreferencesService.isFeedbackWidgetDisabled()) {
    fabContainer.style.display = 'none';
    return;
  }

  if (hasAllowedRole()) {
    fabContainer.style.display = '';
  } else {
    fabContainer.style.display = 'none';
    // Close the form if it was open when the user lost access
    if (isOpen) {
      closeFeedbackForm();
    }
  }
}

/**
 * Dismiss the FAB until the app is restarted (page reload).
 */
function handleDismiss(e: Event): void {
  e.stopPropagation();
  isDismissed = true;
  if (isOpen) {
    closeFeedbackForm();
  }
  updateVisibility();
}

/**
 * Handle FAB button click — toggle form open/closed.
 */
function handleFabClick(): void {
  if (isOpen) {
    closeFeedbackForm();
  } else {
    openFeedbackForm();
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialise the FAB and append it to `document.body`.
 * Listens to `auth-state-changed` to control visibility.
 */
export function initFeedbackFab(): void {
  // Prevent double-init
  if (fabContainer) return;

  // -- Container (holds both button and form panel) --
  fabContainer = document.createElement('div');
  fabContainer.className = 'feedback-fab-container';
  // Start hidden; updateVisibility will show it if appropriate
  fabContainer.style.display = 'none';

  // -- Form panel (rendered above the FAB) --
  formPanel = document.createElement('div');
  formPanel.className = 'feedback-fab__panel';
  formPanel.style.display = 'none';
  fabContainer.appendChild(formPanel);

  // -- FAB button wrapper (for positioning the dismiss button) --
  const fabWrapper = document.createElement('div');
  fabWrapper.className = 'feedback-fab__wrapper';

  // -- Dismiss button (small × at top-right of FAB) --
  dismissButton = document.createElement('button');
  dismissButton.type = 'button';
  dismissButton.className = 'feedback-fab__dismiss';
  dismissButton.setAttribute('aria-label', 'Feedback-Button ausblenden');
  dismissButton.innerHTML = '&times;';
  dismissButton.addEventListener('click', handleDismiss);
  fabWrapper.appendChild(dismissButton);

  // -- FAB button --
  fabButton = document.createElement('button');
  fabButton.type = 'button';
  fabButton.className = 'feedback-fab';
  fabButton.setAttribute('aria-label', 'Feedback geben');
  fabButton.setAttribute('aria-expanded', 'false');
  fabButton.innerHTML = FEEDBACK_ICON;
  fabButton.addEventListener('click', handleFabClick);
  fabWrapper.appendChild(fabButton);

  fabContainer.appendChild(fabWrapper);

  // -- Append to body --
  document.body.appendChild(fabContainer);

  // -- Listen for auth changes --
  unsubscribeAuth = eventBus.on('auth-state-changed', () => {
    updateVisibility();
  });

  // -- Listen for preference changes --
  unsubscribePrefs = userPreferencesService.onPreferencesChanged(() => {
    updateVisibility();
  });

  // Initial visibility check
  updateVisibility();
}

/**
 * Remove the FAB from the DOM and clean up all listeners.
 */
export function destroyFeedbackFab(): void {
  if (unsubscribeAuth) {
    unsubscribeAuth();
    unsubscribeAuth = null;
  }

  if (unsubscribePrefs) {
    unsubscribePrefs();
    unsubscribePrefs = null;
  }

  if (dismissButton) {
    dismissButton.removeEventListener('click', handleDismiss);
    dismissButton = null;
  }

  if (fabButton) {
    fabButton.removeEventListener('click', handleFabClick);
    fabButton = null;
  }

  if (fabContainer) {
    fabContainer.remove();
    fabContainer = null;
  }

  formPanel = null;
  isOpen = false;
  isDismissed = false;
}

/**
 * Force a visibility re-check of the FAB.
 * Call this after session restore to ensure the FAB state is correct.
 */
export function refreshFeedbackFabVisibility(): void {
  updateVisibility();
}

/**
 * Open the feedback form panel above the FAB.
 */
export function openFeedbackForm(): void {
  if (!fabButton || !formPanel || isOpen) return;

  isOpen = true;
  fabButton.setAttribute('aria-expanded', 'true');
  fabButton.innerHTML = CLOSE_ICON;
  fabButton.classList.add('feedback-fab--open');

  formPanel.style.display = '';
  renderFeedbackForm(formPanel, {
    onSuccess: () => {
      closeFeedbackForm();
    },
  });
}

/**
 * Close the feedback form panel.
 */
export function closeFeedbackForm(): void {
  if (!fabButton || !formPanel || !isOpen) return;

  isOpen = false;
  fabButton.setAttribute('aria-expanded', 'false');
  fabButton.innerHTML = FEEDBACK_ICON;
  fabButton.classList.remove('feedback-fab--open');

  resetFeedbackForm(formPanel);
  formPanel.style.display = 'none';
}
